
import React, { useEffect, useRef, useState } from "react";

import {
  FaCar,
  FaMapMarkerAlt,
  FaPlus,
  FaTrash,
  FaRoute,
  FaUsers,
  FaSpinner,
  FaArrowRight,
} from "react-icons/fa";

import {
  computeExpenseDrivingDistance,
} from "../../../services/expenseServices";

import {
  computeCompleteMileage,
} from "../utils/mileageCalculator";

/* ==========================================================================
   HELPERS
   ========================================================================== */

const makeStop = () => ({
  id: crypto.randomUUID(),
  address: "",
  boarding: 1,
  alighting: 0,
  names: "",
});

const money = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const kmLabel = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50";

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export default function MileageRouteCalculator({
  draft,
  setDraft,
  residenceAddress = "",
  computingDistance = false,
  setComputingDistance,
}) {
  const [error, setError] = useState("");
  const revisionRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      revisionRef.current += 1;
    };
  }, []);

  const outboundStops = draft?.mileage_outbound_stops || [];
  const inboundStops = draft?.mileage_inbound_stops || [];

  const roundTrip = draft?.aller_retour ?? true;

  const depart =
    draft?.depart || residenceAddress || "";

  const arrivee =
    draft?.arrivee || draft?.lieu_event || "";

  const result = draft?.mileage_result || null;

  /* ========================================================================
     INVALIDATION DU CALCUL
     ======================================================================== */

  const changeDraft = (updater) => {
    revisionRef.current += 1;
    setError("");
    setComputingDistance(false);

    setDraft((previous) => ({
      ...updater(previous),
      mileage_result: null,
    }));
  };

  /* ========================================================================
     ETAPES
     ======================================================================== */

  const getStopsKey = (direction) =>
    direction === "outbound"
      ? "mileage_outbound_stops"
      : "mileage_inbound_stops";

  const addStop = (direction) => {
    const key = getStopsKey(direction);

    changeDraft((previous) => ({
      ...previous,
      [key]: [
        ...(previous[key] || []),
        makeStop(),
      ],
    }));
  };

  const updateStop = (direction, id, field, value) => {
    const key = getStopsKey(direction);

    changeDraft((previous) => ({
      ...previous,
      [key]: (previous[key] || []).map((stop) =>
        stop.id === id
          ? { ...stop, [field]: value }
          : stop
      ),
    }));
  };

  const removeStop = (direction, id) => {
    const key = getStopsKey(direction);

    changeDraft((previous) => ({
      ...previous,
      [key]: (previous[key] || []).filter(
        (stop) => stop.id !== id
      ),
    }));
  };

  /* ========================================================================
     CALCUL
     ======================================================================== */

  const handleCalculate = async () => {
    const revision = ++revisionRef.current;
    setError("");

    const origin = String(depart).trim();
    const destination = String(arrivee).trim();

    if (!origin || !destination) {
      setError(
        "Renseignez les adresses de départ et d'arrivée."
      );
      return;
    }

    setComputingDistance(true);

    try {
      const result = await computeCompleteMileage({
        outbound: {
          start: origin,
          end: destination,
          initialPassengers: 0,
          stops: outboundStops,
        },

        inbound: {
          start: destination,
          end: origin,
          initialPassengers:
            Number(draft?.mileage_return_passengers ?? 0),
          stops: inboundStops,
        },

        roundTrip,

        computeDistance: computeExpenseDrivingDistance,
      });

      // Ignore le résultat si l'utilisateur a modifié
      // les étapes pendant les appels OpenRouteService.
      if (
        !mountedRef.current ||
        revision !== revisionRef.current
      ) {
        return;
      }

      setDraft((previous) => ({
        ...previous,
        depart: origin,
        arrivee: destination,
        distance_km: String(result.distance_km),
        mileage_result: result,
      }));
    } catch (err) {
      if (
        !mountedRef.current ||
        revision !== revisionRef.current
      ) {
        return;
      }

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Impossible de calculer cet itinéraire."
      );
    } finally {
      if (
        mountedRef.current &&
        revision === revisionRef.current
      ) {
        setComputingDistance(false);
      }
    }
  };

  /* ========================================================================
     RENDU DES ETAPES
     ======================================================================== */

  const renderStops = (direction, stops) => (
    <div className="space-y-3">
      {stops.map((stop, index) => (
        <div
          key={stop.id}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4"
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <FaMapMarkerAlt className="text-indigo-500" />
              Étape {index + 1}
            </div>

            <button
              type="button"
              onClick={() => removeStop(direction, stop.id)}
              className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"
              title="Supprimer cette étape"
              aria-label={`Supprimer l'étape ${index + 1}`}
            >
              <FaTrash size={13} />
            </button>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">
                Adresse de l'arrêt *
              </span>

              <input
                value={stop.address}
                onChange={(e) =>
                  updateStop(
                    direction,
                    stop.id,
                    "address",
                    e.target.value
                  )
                }
                placeholder="Adresse, code postal, ville"
                className={inputClass}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-600">
                  Passagers qui montent
                </span>

                <input
                  type="number"
                  min="0"
                  max="99"
                  step="1"
                  value={stop.boarding}
                  onChange={(e) =>
                    updateStop(
                      direction,
                      stop.id,
                      "boarding",
                      e.target.value
                    )
                  }
                  className={inputClass}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-slate-600">
                  Passagers qui descendent
                </span>

                <input
                  type="number"
                  min="0"
                  max="99"
                  step="1"
                  value={stop.alighting}
                  onChange={(e) =>
                    updateStop(
                      direction,
                      stop.id,
                      "alighting",
                      e.target.value
                    )
                  }
                  className={inputClass}
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">
                Nom des personnes (facultatif)
              </span>

              <input
                value={stop.names}
                onChange={(e) =>
                  updateStop(
                    direction,
                    stop.id,
                    "names",
                    e.target.value
                  )
                }
                placeholder="Ex : Jean Dupont, Marie Martin"
                className={inputClass}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );

  /* ========================================================================
     TABLEAU RESULTATS
     ======================================================================== */

  const renderResults = (journey, title) => {
    if (!journey) return null;

    return (
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="bg-slate-100 px-4 py-3 text-sm font-bold text-slate-800">
          {title}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[550px] text-left text-xs sm:text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-3">Tronçon</th>
                <th className="px-3 py-3 text-right">Km</th>
                <th className="px-3 py-3 text-right">Pass.</th>
                <th className="px-3 py-3 text-right">Taux</th>
                <th className="px-3 py-3 text-right">Montant</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {journey.legs.map((leg, index) => (
                <tr key={index}>
                  <td className="px-3 py-3 text-slate-700">
                    <div className="font-medium">{leg.origin}</div>
                    <div className="mt-1 flex items-center gap-1 text-slate-500">
                      <FaArrowRight size={10} />
                      {leg.destination}
                    </div>
                  </td>

                  <td className="px-3 py-3 text-right">
                    {kmLabel(leg.distance_km)}
                  </td>

                  <td className="px-3 py-3 text-right">
                    {leg.passengers}
                  </td>

                  <td className="px-3 py-3 text-right">
                    {money(leg.rate)} €
                  </td>

                  <td className="px-3 py-3 text-right font-semibold">
                    {money(leg.amount)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap justify-between gap-2 bg-slate-50 px-4 py-3 text-sm">
          <span className="text-slate-600">
            {kmLabel(journey.distance_km)} km
          </span>
          <strong className="text-slate-900">
            {money(journey.montant_trajet)} €
          </strong>
        </div>
      </div>
    );
  };

  /* ========================================================================
     JSX PRINCIPAL
     ======================================================================== */

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-indigo-100 p-3 text-indigo-600">
          <FaCar />
        </div>

        <div>
          <h3 className="font-bold text-slate-900">
            Calcul des indemnités kilométriques
          </h3>

          <p className="mt-1 text-sm leading-6 text-slate-500">
            Indiquez les adresses de votre trajet et les passagers
            pris en charge ou déposés. Les kilomètres et les
            indemnités seront calculés automatiquement.
          </p>
        </div>
      </div>

      {/* ADRESSES PRINCIPALES */}

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <label>
          <span className="mb-1 block text-sm font-semibold text-slate-700">
            Adresse de départ *
          </span>

          <input
            value={depart}
            onChange={(e) =>
              changeDraft((p) => ({
                ...p,
                depart: e.target.value,
              }))
            }
            placeholder="Adresse du domicile"
            className={inputClass}
          />
        </label>

        <label>
          <span className="mb-1 block text-sm font-semibold text-slate-700">
            Lieu de la compétition *
          </span>

          <input
            value={arrivee}
            onChange={(e) =>
              changeDraft((p) => ({
                ...p,
                arrivee: e.target.value,
              }))
            }
            placeholder="Adresse de la compétition"
            className={inputClass}
          />
        </label>
      </div>

      {/* ALLER */}

      <div className="mt-5 rounded-2xl border border-slate-200 p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h4 className="font-bold text-slate-800">
              Trajet aller
            </h4>
            <p className="text-xs text-slate-500">
              Départ du domicile vers la compétition
            </p>
          </div>

          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            ALLER
          </span>
        </div>

        <p className="mb-3 text-sm text-slate-600">
          Vous partez seul. Ajoutez les prises en charge ou
          les déposes éventuelles dans l'ordre du trajet.
        </p>

        {renderStops("outbound", outboundStops)}

        <button
          type="button"
          onClick={() => addStop("outbound")}
          className={`${secondaryButton} mt-3 w-full border-dashed`}
        >
          <FaPlus size={12} />
          Ajouter une étape à l'aller
        </button>
      </div>

      {/* ALLER / RETOUR */}

      <label className="mt-5 flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <input
          type="checkbox"
          checked={roundTrip}
          onChange={(e) =>
            changeDraft((p) => ({
              ...p,
              aller_retour: e.target.checked,
            }))
          }
          className="h-4 w-4 accent-indigo-600"
        />

        <div>
          <div className="text-sm font-semibold text-slate-800">
            Je réalise également le trajet retour
          </div>
          <p className="text-xs text-slate-500">
            Les passagers et les étapes peuvent être différents.
          </p>
        </div>
      </label>

      {/* RETOUR */}

      {roundTrip && (
        <div className="mt-4 rounded-2xl border border-slate-200 p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h4 className="font-bold text-slate-800">
                Trajet retour
              </h4>
              <p className="text-xs text-slate-500">
                Retour de la compétition vers le domicile
              </p>
            </div>

            <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
              RETOUR
            </span>
          </div>

          <label className="mb-4 block">
            <span className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <FaUsers className="text-slate-400" />
              Nombre de passagers au départ du retour
            </span>

            <input
              type="number"
              min="0"
              max="99"
              step="1"
              value={draft?.mileage_return_passengers ?? 0}
              onChange={(e) =>
                changeDraft((p) => ({
                  ...p,
                  mileage_return_passengers: e.target.value,
                }))
              }
              className={inputClass}
            />

            <p className="mt-1 text-xs text-slate-500">
              Ne comptez pas le conducteur.
            </p>
          </label>

          {renderStops("inbound", inboundStops)}

          <button
            type="button"
            onClick={() => addStop("inbound")}
            className={`${secondaryButton} mt-3 w-full border-dashed`}
          >
            <FaPlus size={12} />
            Ajouter une étape au retour
          </button>
        </div>
      )}

      {/* CALCUL */}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleCalculate}
          disabled={computingDistance}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
        >
          {computingDistance ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <FaRoute />
          )}

          {computingDistance
            ? "Calcul des itinéraires..."
            : "Calculer les kilomètres et indemnités"}
        </button>

        <span className="text-xs text-slate-500">
          Distances calculées par OpenRouteService
        </span>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-800"
        >
          {error}
        </div>
      )}

      {/* RESULTATS */}

      {result && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2 font-bold text-slate-800">
            <FaRoute className="text-indigo-600" />
            Détail du calcul
          </div>

          {renderResults(result.aller, "Trajet aller")}

          {roundTrip &&
            renderResults(result.retour, "Trajet retour")}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium uppercase text-slate-500">
                Distance totale parcourue
              </div>
              <div className="mt-1 text-2xl font-black text-slate-900">
                {kmLabel(result.distance_km)} km
              </div>
            </div>

            <div className="rounded-xl bg-indigo-50 p-4">
              <div className="text-xs font-medium uppercase text-indigo-600">
                Indemnités kilométriques
              </div>
              <div className="mt-1 text-2xl font-black text-indigo-900">
                {money(result.montant_trajet)} €
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Taux moyen pondéré : {money(result.taux_moyen)} €/km.
            Le montant est calculé tronçon par tronçon.
          </p>
        </div>
      )}
    </div>
  );
}
